import {
  IAttachmentData,
  IDepartmentData,
  IEmployeeFormSubmissionChecklistData,
  IEmployeeFormSubmissionCommentData,
  IEmployeeFormSubmissionData,
  IMentionAttachmentCommentData,
  IUserData
} from '@/models';
import {
  IEmployeeFormPaginatedListData,
  IEmployeeFormService,
  IUpdateSubmissionChecklistAssigneePayload,
  IEmployeeFormSubmissionFetchParams,
  IUpdateEmployeeFormSubmissionStatePayload
} from './employee-form.types';
import { AxiosInstance } from 'axios';
import { ITransition } from '@/models/transition';
import { objectToFormData } from '@/utils/objectToFormData';

export class EmployeeFormService implements IEmployeeFormService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/forms/submissions/';
  }

  //submissions
  fetchEmployeeFormSubmissions(
    params: IEmployeeFormSubmissionFetchParams
  ): Promise<IEmployeeFormPaginatedListData> {
    return this.axios
      .get<IEmployeeFormPaginatedListData>(`${this.endpoint}`, {
        params
      })
      .then(({ data }) => data);
  }

  fetchEmployeeFormSubmission(
    id: number
  ): Promise<IEmployeeFormSubmissionData> {
    return this.axios
      .get<IEmployeeFormSubmissionData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  updateEmployeeFormSubmissionState(
    data: IUpdateEmployeeFormSubmissionStatePayload
  ): Promise<IEmployeeFormSubmissionData> {
    return this.axios
      .put<IEmployeeFormSubmissionData>(
        `${this.endpoint}${data.id}/set-state/${data.state}/`,
        { comment: data.comment }
      )
      .then(({ data }) => data);
  }

  createEmployeeFormSubmission(
    data: IEmployeeFormSubmissionData
  ): Promise<IEmployeeFormSubmissionData> {
    return this.axios
      .post<IEmployeeFormSubmissionData>(`${this.endpoint}`, data)
      .then(({ data }) => data);
  }

  updateEmployeeFormSubmission(
    data: IEmployeeFormSubmissionData
  ): Promise<IEmployeeFormSubmissionData> {
    return this.axios
      .patch<IEmployeeFormSubmissionData>(`${this.endpoint}${data.id}/`, data)
      .then(({ data }) => data);
  }

  deleteEmployeeFormSubmission(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  exportEmployeeFormSubmissionPDF(id: number): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}${id}/export-pdf/`, {
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }

  // activities
  fetchEmployeeFormSubmissionActivities(id: number): Promise<ITransition[]> {
    return this.axios
      .get<ITransition[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  //checklist
  fetchEmployeeFormSubmissionChecklists(
    data: IEmployeeFormSubmissionData
  ): Promise<IEmployeeFormSubmissionChecklistData[]> {
    return this.axios
      .get<IEmployeeFormSubmissionChecklistData[]>(
        `${this.endpoint}${data.id}/checklist/`
      )
      .then(({ data }) => data);
  }

  updateEmployeeFormSubmissionChecklist(
    data: IEmployeeFormSubmissionChecklistData
  ): Promise<IEmployeeFormSubmissionChecklistData> {
    return this.axios
      .patch<IEmployeeFormSubmissionChecklistData>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  markCompleteEmployeeFormSubmissionChecklist(
    data: IEmployeeFormSubmissionChecklistData
  ) {
    return this.axios
      .post<IEmployeeFormSubmissionChecklistData>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/set-state/`,
        data
      )
      .then(({ data }) => data);
  }

  updateEmployeeFormSubmissionChecklistAssignee(
    data: IUpdateSubmissionChecklistAssigneePayload
  ): Promise<IEmployeeFormSubmissionChecklistData> {
    return this.axios
      .post<IEmployeeFormSubmissionChecklistData>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/set-assignee/`,
        data
      )
      .then(({ data }) => data);
  }

  fetchChecklistAssigneeOptions(data: IEmployeeFormSubmissionChecklistData) {
    return this.axios
      .get<IUserData[]>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/get-assignee-options/`
      )
      .then(({ data }) => data);
  }

  //department
  fetchEmployeeFormSubmissionDepartments(
    data: IEmployeeFormSubmissionData
  ): Promise<IDepartmentData[]> {
    return this.axios
      .get<IDepartmentData[]>(`${this.endpoint}${data.id}/departments/`)
      .then(({ data }) => data);
  }

  // comments
  fetchEmployeeFormSubmissionComments(
    submissionId: number
  ): Promise<IEmployeeFormSubmissionCommentData[]> {
    return this.axios
      .get<IEmployeeFormSubmissionCommentData[]>(
        `${this.endpoint}${submissionId}/comments/`
      )
      .then(({ data }) => data);
  }

  fetchEmployeeFormSubmissionComment(
    submissionId: number,
    commentId: number
  ): Promise<IEmployeeFormSubmissionCommentData> {
    return this.axios
      .get<IEmployeeFormSubmissionCommentData>(
        `${this.endpoint}${submissionId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createEmployeeFormSubmissionComment(
    submissionId: number,
    comment: IEmployeeFormSubmissionCommentData
  ): Promise<IEmployeeFormSubmissionCommentData> {
    return this.axios
      .post<IEmployeeFormSubmissionCommentData>(
        `${this.endpoint}${submissionId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updateEmployeeFormSubmissionComment(
    submissionId: number,
    comment: IEmployeeFormSubmissionCommentData
  ): Promise<IEmployeeFormSubmissionCommentData> {
    return this.axios
      .patch<IEmployeeFormSubmissionCommentData>(
        `${this.endpoint}${submissionId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  deleteEmployeeFormSubmissionComment(
    submissionId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${submissionId}/comments/${commentId}/`)
      .then(({ data }) => data);
  }

  addEmployeeFormSubmissionCommentAttachments(
    submissionId: number,
    commentId: number,
    attachments: IAttachmentData[]
  ): Promise<IMentionAttachmentCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IMentionAttachmentCommentData>(
        `${this.endpoint}${submissionId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeEmployeeFormSubmissionCommentAttachment(
    submissionId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${submissionId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  fetchEmployeeFormSubmissionCommentsMentionableUsers(submissionId: number) {
    return this.axios
      .get(`${this.endpoint}${submissionId}/comments/mentionable-users/`)
      .then(({ data }) => data);
  }
}
